// ABOUTME: Sidebar portfolio dropdown - lists portfolios and sets the active one
// ABOUTME: Collapsed sidebar shows just the briefcase icon; expanded shows the active name

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePortfolios } from '../contexts/PortfolioContext';
import { ChevronDownIcon, PortfolioIcon } from './sidebar-icons';

export function PortfolioSelector({ isCollapsed }: { isCollapsed: boolean }) {
  const { portfolios, activePortfolioId, setActivePortfolio, isLoading } = usePortfolios();
  const [isOpen, setIsOpen] = useState(false);

  const activePortfolio = portfolios.find((p) => p.id === activePortfolioId);

  if (isLoading && portfolios.length === 0) {
    return (
      <div className={`px-3 py-2 text-sm text-neutral-500 ${isCollapsed ? 'text-center' : ''}`}>
        {isCollapsed ? '...' : 'Loading...'}
      </div>
    );
  }

  if (portfolios.length === 0) {
    return null;
  }

  return (
    <div className="relative px-3 py-2">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 w-full px-3 py-2.5 bg-neutral-900 border border-neutral-800 rounded-xl hover:bg-neutral-800 hover:border-neutral-700 transition-colors text-sm ${
          isCollapsed ? 'justify-center' : ''
        }`}
        title={isCollapsed ? activePortfolio?.name || 'Select Portfolio' : undefined}
      >
        <PortfolioIcon className="w-4 h-4 text-neutral-400 flex-shrink-0" />
        {!isCollapsed && (
          <>
            <span className="font-medium text-neutral-200 truncate flex-1 text-left">
              {activePortfolio?.name || 'Select Portfolio'}
            </span>
            <ChevronDownIcon className="w-4 h-4 text-neutral-500 flex-shrink-0" />
          </>
        )}
      </button>

      {isOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-50 cursor-default bg-transparent border-none"
            onClick={() => setIsOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setIsOpen(false)}
            aria-label="Close portfolio selector"
          />
          <div
            className={`absolute top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50 ${
              isCollapsed ? 'left-full ml-2' : 'left-3 right-3'
            }`}
          >
            {portfolios.map((portfolio) => (
              <button
                type="button"
                key={portfolio.id}
                onClick={() => {
                  setActivePortfolio(portfolio.id);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${
                  portfolio.id === activePortfolioId
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-slate-700'
                }`}
              >
                <div className="font-medium">{portfolio.name}</div>
                {portfolio.description && (
                  <div className="text-xs text-slate-500 truncate mt-0.5">
                    {portfolio.description}
                  </div>
                )}
              </button>
            ))}
            <div className="border-t border-slate-100 mt-2 pt-2">
              <Link
                to="/portfolio"
                onClick={() => setIsOpen(false)}
                className="block px-4 py-2 text-sm text-primary-600 hover:bg-slate-50 font-medium"
              >
                Manage Portfolios
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
